import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    CircularProgress,
    LinearProgress
} from '@material-ui/core';

const InviteStudents = ({ open, onClose, cohort }) => {
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [emailSuccess, setEmailSuccess] = useState('');
    const [emailError, setEmailError] = useState('');
    const [memberCount, setMemberCount] = useState(0);

    useEffect(() => {
        if (open && cohort) {
            // Pre-fill with cohort name
            setEmailSubject(`Complete Your ${cohort.name} Survey`);
            setEmailBody(`Hello,

You've been invited to participate in the ${cohort.name} survey. 

Please click the button below to get started. Your feedback is valuable and will help us understand your experience better.

Thank you for your participation!`);
            setMemberCount(cohort.memberCount || 0);
            setEmailError('');
            setEmailSuccess('');
        }
    }, [open, cohort]);

    const handleSend = async () => {
        if (!emailSubject.trim() || !emailBody.trim()) {
            setEmailError('Please fill in both subject and message');
            return;
        }

        setEmailSending(true);
        setEmailError('');
        setEmailSuccess('');

        try {
            // Send the emails via API
            const response = await fetch(`/api/cohortEmail/${cohort.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    subject: emailSubject,
                    message: emailBody,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to send emails');
            }

            // Show success with results
            const { results } = data;
            let successMsg = `Successfully sent ${results.successful} email(s)!`;

            if (results.failed > 0) {
                successMsg += ` (${results.failed} failed)`;
                console.error('Failed emails:', results.errors);
            }

            setEmailSuccess(successMsg);

            // Close after 2 seconds if all successful
            if (results.failed === 0) {
                setTimeout(() => {
                    onClose();
                    setEmailSuccess('');
                }, 2000);
            }

        } catch (error) {
            console.error('Error sending emails:', error);
            setEmailError(error.message || 'Failed to send emails. Please try again.');
        } finally {
            setEmailSending(false);
        }
    };

    const handleClose = () => {
        if (!emailSending) {
            setEmailSubject('');
            setEmailBody('');
            setEmailError('');
            setEmailSuccess('');
            onClose();
        }
    };

    const surveyLink = cohort ? `${window.location.origin}/questions?cohortId=${cohort.id}` : '';

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Email Members: {cohort?.name}
            </DialogTitle>
            <DialogContent>
                {emailSending && (
                    <Box mb={2}>
                        <Typography variant="body2" gutterBottom>
                            Sending emails...
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}

                <Box mb={2} p={2} style={{ backgroundColor: '#f0f4ff', borderRadius: '4px' }}>
                    <Typography variant="body2">
                        <strong>Cohort:</strong> {cohort?.name}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Members:</strong> {memberCount}
                    </Typography>
                    <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                        <strong>Survey Link:</strong>{' '}
                        <a href={surveyLink} target="_blank" rel="noopener noreferrer">
                            {surveyLink}
                        </a>
                    </Typography>
                </Box>

                <TextField
                    label="Subject Line"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    disabled={emailSending}
                />

                <TextField
                    label="Message Body"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    fullWidth
                    multiline
                    rows={10}
                    variant="outlined"
                    margin="normal"
                    disabled={emailSending}
                    placeholder="Write your message here. The survey link will be automatically included as a button in the email."
                />

                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                    💡 The survey link will be automatically formatted as a button in the email.
                </Typography>

                {emailError && (
                    <Box mt={2} p={2} style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
                        <Typography style={{ color: '#721c24' }}>
                            ✗ {emailError}
                        </Typography>
                    </Box>
                )}

                {emailSuccess && (
                    <Box mt={2} p={2} style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
                        <Typography style={{ color: '#155724' }}>
                            ✓ {emailSuccess}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={emailSending}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSend}
                    color="primary"
                    variant="contained"
                    disabled={emailSending || !emailSubject.trim() || !emailBody.trim()}
                >
                    {emailSending ? (
                        <>
                            <CircularProgress size={20} style={{ marginRight: 8 }} />
                            Sending...
                        </>
                    ) : (
                        `Send to ${memberCount} member${memberCount !== 1 ? 's' : ''}`
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InviteStudents;